import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/lib/auth-store";
import type { AdminPermission, FeatureAccessMap, FeatureKey } from "@/types/auth";
import { setLang, type SupportedLang } from "@/i18n/i18n";
import { toggleTheme, useTheme } from "@/lib/theme";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchNotifications,
  fetchUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
} from "@/features/notifications/api";
import { NotificationIcon } from "@/features/notifications/NotificationIcon";

type AdminCategory = "people" | "schedule" | "insights" | "automation";

interface NavItem {
  href: string;
  labelKey: string;
  match: (path: string) => boolean;
}
interface AdminItem extends NavItem {
  category: AdminCategory;
  permission?: AdminPermission;
  superadminOnly?: boolean;
  featureGate?: { feature: FeatureKey };
}

const startsWith = (prefix: string) => (p: string) => p.startsWith(prefix);

function buildUserLinks(features: FeatureAccessMap): NavItem[] {
  const visible = (k: FeatureKey) => features[k]?.visible ?? true;
  const links: NavItem[] = [];
  if (visible("shifts")) links.push({ href: "/shifts/my", labelKey: "nav.shifts", match: startsWith("/shifts") });
  if (visible("leaves")) links.push({ href: "/leaves/my", labelKey: "nav.leaves", match: startsWith("/leaves") });
  if (visible("documents")) links.push({ href: "/documents", labelKey: "nav.documents", match: startsWith("/documents") });
  if (visible("tasks")) links.push({ href: "/tasks/my", labelKey: "nav.tasks", match: startsWith("/tasks") });
  links.push({ href: "/profile", labelKey: "nav.profile", match: startsWith("/profile") });
  return links;
}

const ADMIN_ITEMS: AdminItem[] = [
  { href: "/admin/users", labelKey: "nav.admin.users", match: startsWith("/admin/users"), category: "people", permission: "ADMIN_USERS" },
  { href: "/admin/settings", labelKey: "nav.admin.settings", match: startsWith("/admin/settings"), category: "people", permission: "ADMIN_USERS" },
  { href: "/admin/worker-types", labelKey: "nav.admin.types", match: startsWith("/admin/worker-types"), category: "people", permission: "ADMIN_TYPES" },
  { href: "/admin/shifts", labelKey: "nav.admin.shifts", match: startsWith("/admin/shifts"), category: "schedule", permission: "ADMIN_SHIFTS" },
  { href: "/admin/shift-events", labelKey: "nav.admin.events", match: startsWith("/admin/shift-events"), category: "schedule", permission: "ADMIN_SHIFTS" },
  { href: "/admin/leaves", labelKey: "nav.admin.leaves", match: startsWith("/admin/leaves"), category: "schedule", permission: "ADMIN_LEAVES" },
  { href: "/admin/sick-leaves", labelKey: "nav.admin.sick_leaves", match: startsWith("/admin/sick-leaves"), category: "schedule", permission: "ADMIN_SICK_LEAVES" },
  { href: "/admin/absences", labelKey: "nav.admin.absences", match: startsWith("/admin/absences"), category: "schedule", permission: "ADMIN_ABSENCES" },
  { href: "/admin/schedules", labelKey: "nav.admin.schedules", match: startsWith("/admin/schedules"), category: "schedule", permission: "ADMIN_SCHEDULES" },
  { href: "/admin/holidays", labelKey: "nav.admin.holidays", match: startsWith("/admin/holidays"), category: "schedule", permission: "MANAGE_HOLIDAYS" },
  { href: "/admin/timeline", labelKey: "nav.admin.timeline", match: startsWith("/admin/timeline"), category: "insights", permission: "ADMIN_SUMMARY_WEEKLY" },
  { href: "/admin/summary", labelKey: "nav.admin.summary", match: startsWith("/admin/summary"), category: "insights", permission: "ADMIN_SUMMARY_WEEKLY", featureGate: { feature: "reports" } },
  { href: "/admin/registro-legal", labelKey: "nav.admin.legal_registry", match: startsWith("/admin/registro-legal"), category: "insights", permission: "ADMIN_SUMMARY_WEEKLY", featureGate: { feature: "reports" } },
  { href: "/admin/auto-rules", labelKey: "nav.admin.autoshift", match: startsWith("/admin/auto-rules"), category: "automation", permission: "ADMIN_AUTOSHIFT" },
  { href: "/admin/tasks", labelKey: "nav.admin.tasks", match: startsWith("/admin/tasks"), category: "automation", permission: "ADMIN_TASKS" },
  { href: "/admin/faltas", labelKey: "nav.admin.faltas", match: startsWith("/admin/faltas"), category: "automation", permission: "MANAGE_INFRACTIONS" },
  { href: "/admin/documents", labelKey: "nav.admin.documents", match: startsWith("/admin/documents"), category: "people", permission: "ADMIN_DOCUMENTS", featureGate: { feature: "admin_documents" } },
  { href: "/admin/features", labelKey: "nav.admin.features", match: startsWith("/admin/features"), category: "people", superadminOnly: true },
  { href: "/admin/passwords", labelKey: "nav.admin.passwords", match: startsWith("/admin/passwords"), category: "people", permission: "ADMIN_PASSWORD_RESET" },
  { href: "/admin/tablets", labelKey: "nav.admin.tablets", match: startsWith("/admin/tablets"), category: "people", permission: "TABLET_MANAGE_DEVICES" },
  { href: "/admin/locations", labelKey: "nav.admin.locations", match: startsWith("/admin/locations"), category: "people", permission: "TABLET_MANAGE_DEVICES" },
];

const ADMIN_CATEGORIES: { key: AdminCategory; titleKey: string }[] = [
  { key: "people", titleKey: "nav.cat.people" },
  { key: "schedule", titleKey: "nav.cat.schedule" },
  { key: "insights", titleKey: "nav.cat.insights" },
  { key: "automation", titleKey: "nav.cat.automation" },
];

function filterAdminItems(
  items: AdminItem[],
  isSuperadmin: boolean,
  hasPermission: (p: AdminPermission) => boolean,
  features: FeatureAccessMap
): AdminItem[] {
  return items.filter((item) => {
    if (item.superadminOnly && !isSuperadmin) return false;
    if (item.permission && !isSuperadmin && !hasPermission(item.permission)) return false;
    if (item.featureGate) {
      const f = features[item.featureGate.feature];
      if (f && (!f.visible || !f.enabled)) return false;
    }
    return true;
  });
}

function ThemeIcons() {
  return (
    <>
      <span className="theme-icon theme-icon--dark" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3a1 1 0 0 1 1 1v1.5a1 1 0 0 1-2 0V4a1 1 0 0 1 1-1Z" />
          <path d="M12 18.5a1 1 0 0 1 1 1V21a1 1 0 0 1-2 0v-1.5a1 1 0 0 1 1-1Z" />
          <path d="M4 11a1 1 0 0 1 1-1h1.5a1 1 0 1 1 0 2H5a1 1 0 0 1-1-1Z" />
          <path d="M17.5 10a1 1 0 1 1 0 2H19a1 1 0 1 1 0-2h-1.5Z" />
          <path d="M6.2 6.2a1 1 0 0 1 1.4 0l1.1 1.1a1 1 0 1 1-1.4 1.4L6.2 7.6a1 1 0 0 1 0-1.4Z" />
          <path d="M15.3 15.3a1 1 0 0 1 1.4 0l1.1 1.1a1 1 0 1 1-1.4 1.4l-1.1-1.1a1 1 0 0 1 0-1.4Z" />
          <path d="M6.2 17.8a1 1 0 0 1 0-1.4l1.1-1.1a1 1 0 1 1 1.4 1.4l-1.1 1.1a1 1 0 0 1-1.4 0Z" />
          <path d="M15.3 8.7a1 1 0 0 1 0-1.4l1.1-1.1a1 1 0 1 1 1.4 1.4l-1.1 1.1a1 1 0 0 1-1.4 0Z" />
          <circle cx="12" cy="12" r="4" />
        </svg>
      </span>
      <span className="theme-icon theme-icon--light" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 14.5A8.5 8.5 0 1 1 9.5 3a7 7 0 0 0 11.5 11.5Z" />
        </svg>
      </span>
    </>
  );
}

function LangButton({ className }: { className: string }) {
  const { i18n, t } = useTranslation();
  const current = (i18n.language as SupportedLang) || "es";
  const next: SupportedLang = current === "en" ? "es" : "en";
  return (
    <button
      type="button"
      className={className}
      aria-label={t("nav.lang.switch")}
      onClick={() => setLang(next)}
    >
      <span className="topbar__lang-current">{current.toUpperCase()}</span>
      <span className="topbar__lang-divider">/</span>
      <span className="topbar__lang-next">{next.toUpperCase()}</span>
    </button>
  );
}

interface MenuGroupProps {
  id: string;
  triggerLabel: string;
  active?: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  children: React.ReactNode;
  menuClassName?: string;
}

function MenuGroup({ id, triggerLabel, active, isOpen, onToggle, onClose, children, menuClassName = "topbar__menu" }: MenuGroupProps) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("click", handler);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("click", handler);
      document.removeEventListener("keydown", esc);
    };
  }, [isOpen, onClose]);

  return (
    <div ref={ref} className={"topbar__group" + (active ? " is-active" : "") + (isOpen ? " is-open" : "")}>
      <button
        type="button"
        className="topbar__pill topbar__pill--menu"
        aria-controls={id}
        aria-expanded={isOpen}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
      >
        <span>{triggerLabel}</span>
        <span className="topbar__chevron" aria-hidden="true">▾</span>
      </button>
      <div className={menuClassName} id={id} role="menu" onClick={onClose}>
        {children}
      </div>
    </div>
  );
}

function NotifBell({
  isOpen,
  onToggle,
  onClose,
}: {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("es") ? "es-ES" : "en-US";
  const authStatus = useAuthStore((s) => s.status);
  const isAuthenticated = authStatus === "authenticated";
  const qc = useQueryClient();
  const bellRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) onClose();
    };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", esc);
    };
  }, [isOpen, onClose]);

  const unreadQuery = useQuery({
    queryKey: ["notif-unread"],
    queryFn: fetchUnreadCount,
    enabled: isAuthenticated,
    refetchInterval: isAuthenticated ? 30_000 : false,
    staleTime: 15_000,
  });

  const recentQuery = useQuery({
    queryKey: ["notif-recent"],
    queryFn: () => fetchNotifications({ limit: 5, offset: 0 }),
    enabled: isOpen && isAuthenticated,
    staleTime: 15_000,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notif-unread"] });
      qc.invalidateQueries({ queryKey: ["notif-recent"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const markAll = useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notif-unread"] });
      qc.invalidateQueries({ queryKey: ["notif-recent"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const unread = unreadQuery.data ?? 0;
  const recent = recentQuery.data?.notifications ?? [];

  return (
    <div ref={bellRef} className={`topbar__group topbar__notif-group${isOpen ? " is-open" : ""}`}>
      <button
        type="button"
        className={
          "topbar__pill topbar__pill--notif" + (unread > 0 ? " has-unread" : "")
        }
        aria-label={t("notif.bell_aria")}
        aria-expanded={isOpen}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
      >
        <svg
          className="notif-bell-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          width="20"
          height="20"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 && (
          <span className="notif-badge" aria-hidden="true">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {isOpen && (
          <div
            className="topbar__menu topbar__menu--right topbar__notif-dropdown is-open"
            role="menu"
            onClick={(e) => e.stopPropagation()}
          >
          <div className="notif-dropdown-header">
            <strong>{t("notif.title")}</strong>
            <button
              type="button"
              className="notif-mark-all-btn"
              disabled={unread === 0 || markAll.isPending}
              onClick={() => markAll.mutate()}
            >
              {t("notif.mark_all")}
            </button>
          </div>
          <div className="notif-dropdown-list">
            {recentQuery.isLoading && (
              <div className="notif-dropdown-empty">…</div>
            )}
            {!recentQuery.isLoading && recent.length === 0 && (
              <div className="notif-dropdown-empty">{t("notif.empty")}</div>
            )}
            {recent.map((n) => (
              <Link
                key={n.id}
                to="/notifications"
                className={
                  "notification-item" + (!n.read_at ? " notification-unread" : "")
                }
                onClick={() => {
                  if (!n.read_at) markRead.mutate(n.id);
                  onClose();
                }}
                style={{ display: "flex", padding: 10, gap: 8 }}
              >
                <NotificationIcon category={n.category} />
                <div className="notification-content">
                  <div className="notification-title-row">
                    {!n.read_at && <span className="notification-dot" />}
                    <strong style={{ fontSize: 13 }}>{n.title}</strong>
                  </div>
                  <div
                    className="notification-body"
                    style={{ fontSize: 12, marginTop: 2 }}
                  >
                    {n.body}
                  </div>
                  <div
                    className="summary-card-meta"
                    style={{ fontSize: 11, marginTop: 2 }}
                  >
                    {formatRelative(n.created_at, locale)}
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <Link
            to="/notifications"
            className="notif-dropdown-footer"
            onClick={onClose}
          >
            {t("nav.notif.view_all", { defaultValue: "Ver todas" })} →
          </Link>
        </div>
      )}
    </div>
  );
}

function formatRelative(iso: string, locale: string): string {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return locale.startsWith("es") ? "ahora mismo" : "just now";
    if (diff < 3600) {
      const m = Math.floor(diff / 60);
      return locale.startsWith("es") ? `hace ${m} min` : `${m} min ago`;
    }
    if (diff < 86400) {
      const h = Math.floor(diff / 3600);
      return locale.startsWith("es") ? `hace ${h} h` : `${h}h ago`;
    }
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function Topbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const user = useAuthStore((s) => s.user);
  const features = useAuthStore((s) => s.features);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const logout = useAuthStore((s) => s.logout);

  const [openMenu, setOpenMenu] = useState<null | "user-services" | "admin-services" | "user" | "notif">(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);

  // Close menus on navigation
  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
  }, [location.pathname]);

  const path = location.pathname;
  const isAuthed = !!user;
  const isAdmin = !!user?.is_admin;
  const isSuperadmin = !!user?.is_superadmin;

  const userLinks = isAuthed ? buildUserLinks(features) : [];
  const adminItems = isAdmin ? filterAdminItems(ADMIN_ITEMS, isSuperadmin, hasPermission, features) : [];

  const userSectionActive = userLinks.some((l) => l.match(path));
  const adminSectionActive = adminItems.some((l) => l.match(path));

  const userEmail = user?.email ?? "";
  const userInitial = (userEmail || "U").slice(0, 1).toUpperCase();
  const avatarUrl = user?.avatar_thumb_url ?? null;

  useEffect(() => {
    setAvatarLoadFailed(false);
  }, [avatarUrl]);

  const themeLabel = theme === "dark" ? t("nav.theme.dark") : t("nav.theme.light");

  const onLogout = async () => {
    await logout();
    navigate("/auth/login", { replace: true });
  };

  const toggle = (key: typeof openMenu) => setOpenMenu((cur) => (cur === key ? null : key));
  const closeAll = () => setOpenMenu(null);

  const brandHref = isAuthed ? "/shifts/my" : "/auth/login";

  return (
    <header className="app-header topbar">
      <div className="app-header-inner topbar__inner">
        <Link to={brandHref} className="topbar__brand">
          <img src="/static/img/favicon_32.png" alt="Trust in Time" className="topbar__logo" />
          <span className="topbar__brand-text">Trust in Time</span>
        </Link>

        <button
          type="button"
          className="topbar__toggle"
          aria-controls="topbar-mobile"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((v) => !v)}
        >
          <span className="sr-only">{t("nav.menu")}</span>
          <span className="topbar__toggle-icon" aria-hidden="true">
            <span></span><span></span><span></span>
          </span>
        </button>

        <nav className="topbar__nav" aria-label="Primary">
          <div className="topbar__left">
            {isAuthed && userLinks.length > 0 && (
              <MenuGroup
                id="menu-user-services"
                triggerLabel={t("nav.my_services")}
                active={userSectionActive}
                isOpen={openMenu === "user-services"}
                onToggle={() => toggle("user-services")}
                onClose={closeAll}
              >
                <div className="topbar__menu-list">
                  {userLinks.map((l) => (
                    <Link
                      key={l.href}
                      to={l.href}
                      className={"topbar__menu-item" + (l.match(path) ? " is-active" : "")}
                    >
                      <span className="topbar__menu-icon" aria-hidden="true"></span>
                      <span className="topbar__menu-text">{t(l.labelKey)}</span>
                    </Link>
                  ))}
                </div>
              </MenuGroup>
            )}

            {isAuthed && adminItems.length > 0 && (
              <MenuGroup
                id="menu-admin-services"
                triggerLabel={t("nav.admin_services")}
                active={adminSectionActive}
                isOpen={openMenu === "admin-services"}
                onToggle={() => toggle("admin-services")}
                onClose={closeAll}
                menuClassName="topbar__menu topbar__menu--mega"
              >
                <div className="topbar__menu-grid">
                  {ADMIN_CATEGORIES.map((col) => {
                    const items = adminItems.filter((i) => i.category === col.key);
                    if (items.length === 0) return null;
                    return (
                      <div key={col.key} className="topbar__menu-col">
                        <div className="topbar__menu-heading">{t(col.titleKey)}</div>
                        {items.map((it) => (
                          <Link
                            key={it.href}
                            to={it.href}
                            className={"topbar__menu-item" + (it.match(path) ? " is-active" : "")}
                          >
                            <span className="topbar__menu-text">{t(it.labelKey)}</span>
                          </Link>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </MenuGroup>
            )}

            {isSuperadmin && (
              <>
                <Link
                  to="/admin/permissions"
                  className={"topbar__pill" + (path.startsWith("/admin/permissions") ? " is-active" : "")}
                >
                  {t("nav.permissions")}
                </Link>
                <Link
                  to="/admin/users/manage"
                  className={"topbar__pill" + (path.startsWith("/admin/users/manage") ? " is-active" : "")}
                >
                  {t("nav.manage_users")}
                </Link>
              </>
            )}
          </div>
        </nav>

        <div className="topbar__right">
          <button
            type="button"
            className="topbar__pill topbar__pill--theme"
            aria-pressed={theme === "light"}
            onClick={toggleTheme}
            title={themeLabel}
          >
            <ThemeIcons />
          </button>

          <LangButton className="topbar__pill topbar__pill--lang" />

          {isAuthed ? (
            <>
              <NotifBell
                isOpen={openMenu === "notif"}
                onToggle={() => toggle("notif")}
                onClose={closeAll}
              />

              <div className={"topbar__group" + (openMenu === "user" ? " is-open" : "")}>
                <button
                  type="button"
                  className="topbar__pill topbar__pill--user"
                  aria-controls="menu-user"
                  aria-expanded={openMenu === "user"}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle("user");
                  }}
                >
                  {avatarUrl && !avatarLoadFailed ? (
                    <img src={avatarUrl} alt={t("nav.avatar")} className="topbar__avatar topbar__avatar--img" onError={() => setAvatarLoadFailed(true)} />
                  ) : (
                    <span className="topbar__avatar">{userInitial}</span>
                  )}
                  <span className="topbar__user-email">{user?.name || userEmail}</span>
                  <span className="topbar__chevron" aria-hidden="true">▾</span>
                </button>
                {openMenu === "user" && (
                  <div className="topbar__menu topbar__menu--right" id="menu-user" role="menu">
                    <Link to="/profile" className="topbar__menu-item" onClick={closeAll}>
                      {t("nav.profile")}
                    </Link>
                    <button
                      type="button"
                      className="topbar__menu-item topbar__menu-button"
                      onClick={onLogout}
                    >
                      {t("nav.logout")}
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <Link
              to="/auth/login"
              className={"topbar__pill" + (path.startsWith("/auth/login") ? " is-active" : "")}
            >
              {t("nav.login")}
            </Link>
          )}
        </div>
      </div>

      <div className={"topbar__mobile" + (mobileOpen ? " is-open" : "")} id="topbar-mobile">
        <div className="topbar__mobile-inner">
          {userLinks.length > 0 && (
            <div className="topbar__mobile-group">
              <div className="topbar__mobile-group-header">
                <svg className="topbar__mobile-group-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <span>{t("nav.my_services")}</span>
              </div>
              <div className="topbar__mobile-group-links">
                {userLinks.map((l) => (
                  <Link
                    key={l.href}
                    to={l.href}
                    className={"topbar__mobile-link" + (l.match(path) ? " is-active" : "")}
                  >
                    {t(l.labelKey)}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {adminItems.length > 0 && (
            <div className="topbar__mobile-group topbar__mobile-group--admin">
              <details className="topbar__mobile-details">
                <summary className="topbar__mobile-group-header topbar__mobile-group-header--toggle">
                  <svg className="topbar__mobile-group-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  <span>{t("nav.admin_panel")}</span>
                  <svg className="topbar__mobile-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </summary>
                <div className="topbar__mobile-details-body">
                  {ADMIN_CATEGORIES.map((col) => {
                    const items = adminItems.filter((i) => i.category === col.key);
                    if (items.length === 0) return null;
                    return (
                      <div key={col.key} className="topbar__mobile-category">
                        <div className="topbar__mobile-category-title">{t(col.titleKey)}</div>
                        <div className="topbar__mobile-group-links">
                          {items.map((it) => (
                            <Link
                              key={it.href}
                              to={it.href}
                              className={"topbar__mobile-link" + (it.match(path) ? " is-active" : "")}
                            >
                              {t(it.labelKey)}
                            </Link>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </details>
            </div>
          )}

          {isSuperadmin && (
            <div className="topbar__mobile-group topbar__mobile-group--admin">
              <div className="topbar__mobile-group-header">
                <svg className="topbar__mobile-group-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                <span>{t("nav.superadmin")}</span>
              </div>
              <div className="topbar__mobile-group-links">
                <Link
                  to="/admin/permissions"
                  className={"topbar__mobile-link" + (path.startsWith("/admin/permissions") ? " is-active" : "")}
                >
                  {t("nav.permissions_short")}
                </Link>
                <Link
                  to="/admin/users/manage"
                  className={"topbar__mobile-link" + (path.startsWith("/admin/users/manage") ? " is-active" : "")}
                >
                  {t("nav.manage_users_short")}
                </Link>
              </div>
            </div>
          )}

          <div className="topbar__mobile-separator"></div>

          <div className="topbar__mobile-utilities">
            <button
              type="button"
              className="topbar__mobile-util-btn"
              aria-pressed={theme === "light"}
              onClick={toggleTheme}
            >
              <ThemeIcons />
            </button>
            <LangButton className="topbar__mobile-util-btn" />
          </div>

          {isAuthed ? (
            <div className="topbar__mobile-account">
              <Link to="/profile" className="topbar__mobile-link topbar__mobile-link--profile">
                {avatarUrl && !avatarLoadFailed ? (
                  <img src={avatarUrl} alt="" className="topbar__mobile-avatar topbar__mobile-avatar--img" onError={() => setAvatarLoadFailed(true)} />
                ) : (
                  <span className="topbar__mobile-avatar">{userInitial}</span>
                )}
                <span>{t("nav.profile")}</span>
              </Link>
              <button
                type="button"
                className="topbar__mobile-link topbar__mobile-link-button topbar__mobile-link--logout"
                onClick={onLogout}
              >
                {t("nav.logout")}
              </button>
            </div>
          ) : (
            <div className="topbar__mobile-account">
              <Link
                to="/auth/login"
                className={"topbar__mobile-link" + (path.startsWith("/auth/login") ? " is-active" : "")}
              >
                {t("nav.login")}
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
